import React from 'react';
import { Card, LineChart, Text } from '@tremor/react';
import { useTranslation } from 'react-i18next';

function AudioEmotionSentimentTimelineCard({ fragment, ...rest }) {
    const { t } = useTranslation();

    // Handle missing fragment
    if (!fragment || fragment.status !== 'completed' || !fragment.result) {
        return (
            <Card className="max-h-full" data-testid="audio-emotion-sentiment-timeline-card" {...rest}>
                <div className="p-4">
                    <Text className="text-yellow-600">
                        {t('AudioEmotionSentiment.TimelineNotReady', 'Audio emotion timeline not ready')}
                    </Text>
                    <div className="mt-2 text-xs text-gray-500">
                        Status: {fragment?.status || 'missing'}<br/>
                        Has result: {fragment?.result ? 'yes' : 'no'}
                    </div>
                </div>
            </Card>
        );
    }

    try {
        const results = typeof fragment.result === 'string' ? JSON.parse(fragment.result) : fragment.result;
        
        if (!results || typeof results !== 'object') {
            return (
                <Card className="max-h-full" data-testid="audio-emotion-sentiment-timeline-card" {...rest}>
                    <div className="p-4">
                        <Text className="text-red-600">
                            {t('AudioEmotionSentiment.TimelineInvalidData', 'Invalid timeline data')}
                        </Text>
                        <div className="mt-2 text-xs text-gray-500">
                            Data type: {typeof results}
                        </div>
                    </div>
                </Card>
            );
        }
        
        // Look for time series data in different possible locations
        let timeSeriesResults = null;
        
        // DEBUG: Log the data structure to console
        console.log('AudioEmotionSentimentTimelineCard - Debug data:', {
            'results keys': Object.keys(results),
            'time_series_results length': results.time_series_results?.length,
            'overall_results.time_series length': results.overall_results?.time_series?.length,
            'overall_results keys': results.overall_results ? Object.keys(results.overall_results) : 'none'
        });
        
        if (results.time_series_results && Array.isArray(results.time_series_results) && results.time_series_results.length > 0) {
            timeSeriesResults = results.time_series_results;
            console.log('Using time_series_results:', timeSeriesResults.length);
        } else if (results.overall_results?.time_series && Array.isArray(results.overall_results.time_series) && results.overall_results.time_series.length > 0) {
            // NEW: Check for time series data in overall_results.time_series (from new webhook format)
            timeSeriesResults = results.overall_results.time_series;
            console.log('Using overall_results.time_series:', timeSeriesResults.length);
        } else if (results.time_series && Array.isArray(results.time_series) && results.time_series.length > 0) {
            timeSeriesResults = results.time_series;
            console.log('Using time_series:', timeSeriesResults.length);
        } else if (results.overall_results?.segments && Array.isArray(results.overall_results.segments)) {
            // Convert overall_results segments to time series format if they have timestamp info
            timeSeriesResults = results.overall_results.segments.filter(seg => 
                seg.start_time !== undefined || seg.second !== undefined || seg.timestamp !== undefined
            );
        } else if (results.segments && Array.isArray(results.segments)) {
            // Convert segments to time series format if they have timestamp info
            timeSeriesResults = results.segments.filter(seg => 
                seg.start_time !== undefined || seg.second !== undefined || seg.timestamp !== undefined
            );
        }
        
        if (!timeSeriesResults || timeSeriesResults.length === 0) {
            // This is normal - some emotion analysis results don't have time series data
            return (
                <Card className="max-h-full" data-testid="audio-emotion-sentiment-timeline-card" {...rest}>
                    <div className="p-4">
                        <Text className="text-gray-600">
                            {t('AudioEmotionSentiment.NoTimelineData', 'No timeline data available')}
                        </Text>
                        <div className="mt-2 text-xs text-gray-500">
                            Available data types:<br/>
                            - Time series: {results.time_series ? `${results.time_series.length} points` : 'none'}<br/>
                            - Time series results: {results.time_series_results ? `${results.time_series_results.length} points` : 'none'}<br/>
                            - Overall results time_series: {results.overall_results?.time_series ? `${results.overall_results.time_series.length} points` : 'none'}<br/>
                            - Overall results segments: {results.overall_results?.segments ? `${results.overall_results.segments.length} segments` : 'none'}<br/>
                            - Root level segments: {results.segments ? `${results.segments.length} segments` : 'none'}<br/>
                            - Time series found: {timeSeriesResults ? `${timeSeriesResults.length} points` : 'none'}
                        </div>
                    </div>
                </Card>
            );
        }

        // Generate emotion timeline data with translated labels
        const timelineData = generateEmotionTimelineData(timeSeriesResults, t);

        if (timelineData.length === 0) {
            return (
                <Card className="max-h-full" data-testid="audio-emotion-sentiment-timeline-card" {...rest}>
                    <div className="p-4">
                        <Text className="text-red-600">
                            {t('AudioEmotionSentiment.NoTimelineGenerated', 'Could not generate timeline')}
                        </Text>
                        <div className="mt-2 text-xs text-gray-500">
                            Time series points: {timeSeriesResults.length}<br/>
                            Generated data points: {timelineData.length}
                        </div>
                    </div>
                </Card>
            );
        }

        // Emotion categories with translation keys for the chart
        const emotionCategories = [
            t('AudioEmotionSentiment.Emotions.Happiness', 'Happiness'),
            t('AudioEmotionSentiment.Emotions.Sadness', 'Sadness'), 
            t('AudioEmotionSentiment.Emotions.Anger', 'Anger'),
            t('AudioEmotionSentiment.Emotions.Fear', 'Fear'),
            t('AudioEmotionSentiment.Emotions.Surprise', 'Surprise'),
            t('AudioEmotionSentiment.Emotions.Disgust', 'Disgust'),
            t('AudioEmotionSentiment.Emotions.Neutral', 'Neutral')
        ];

        return (
            <Card className="max-h-full" data-testid="audio-emotion-sentiment-timeline-card" {...rest}>
                <div className="p-4">
                    <Text className="mb-4 font-medium text-gray-900">
                        {t('AudioEmotionSentiment.EmotionsOverTime', 'Emotions Over Time')}
                    </Text>
                    
                    <div className="relative">
                        <LineChart
                            className="h-48 mb-4"
                            data={timelineData}
                            index="timeRange"
                            categories={emotionCategories}
                            colors={["green-500", "blue-500", "red-500", "purple-500", "amber-500", "lime-500", "gray-500"]}
                            valueFormatter={(value) => `${value.toFixed(2)}%`}
                            showLegend={false}
                            showAnimation={false}
                            connectNulls={false}
                            curveType="linear"
                            showDots={true}
                            noDataText={t('AudioEmotionSentiment.NoTimelineDataAvailable', 'No timeline data available')}
                        />
                        
                        {/* Custom legend with rounded squares positioned at bottom */}
                        <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-2">
                            {emotionCategories.map((emotion, index) => {
                                const colors = ["bg-green-500", "bg-blue-500", "bg-red-500", "bg-purple-500", "bg-amber-500", "bg-lime-500", "bg-gray-500"];
                                return (
                                    <div key={emotion} className="flex items-center gap-2">
                                        <div 
                                            className={`w-3 h-3 rounded-sm ${colors[index]}`}
                                        ></div>
                                        <span className="text-sm text-gray-700">
                                            {emotion}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    
                    <Text className="mt-2 text-xs text-gray-500">
                        {t('AudioEmotionSentiment.TimelineNote', 'Emotion distribution in 3-second intervals')}
                    </Text>
                </div>
            </Card>
        );
    } catch (error) {
        return (
            <Card className="max-h-full" data-testid="audio-emotion-sentiment-timeline-card" {...rest}>
                <div className="p-4">
                    <Text className="text-red-600">
                        {t('AudioEmotionSentiment.TimelineError', 'Error processing timeline data')}
                    </Text>
                    <div className="mt-2 text-xs text-gray-500">
                        Error: {error.message}<br/>
                        Stack: {error.stack?.substring(0, 200)}...<br/>
                        Fragment status: {fragment?.status}<br/>
                        Fragment result type: {typeof fragment?.result}
                    </div>
                </div>
            </Card>
        );
    }
}

// Helper functions for emotion timeline generation

function generateEmotionTimelineData(timeSeriesResults, t) {
    if (!Array.isArray(timeSeriesResults) || timeSeriesResults.length === 0) {
        console.warn('generateEmotionTimelineData: Invalid timeSeriesResults:', timeSeriesResults);
        return [];
    }
    
    try {
        const intervalDuration = 3; // 3-second intervals for detailed emotion tracking
        
        // Filter out invalid data points and normalize different time formats
        const validPoints = timeSeriesResults.filter(point => {
            if (!point) return false;
            
            // Check for different time field names and emotion formats
            const hasTime = typeof point.second === 'number' || 
                           typeof point.timestamp === 'number' || 
                           typeof point.start_time === 'number';
            const hasEmotion = point.dominant_emotion || 
                             point.emotion || 
                             point.emotions ||
                             point.emotion_analysis;
            
            return hasTime && hasEmotion;
        }).map(point => {
            // Normalize the data format
            const timeValue = point.second || point.timestamp || point.start_time || 0;
            let emotionValue = null;
            
            if (point.dominant_emotion) {
                emotionValue = point.dominant_emotion;
            } else if (point.emotion) {
                emotionValue = point.emotion;
            } else if (point.emotions) {
                // Find the dominant emotion from emotions object
                const emotions = point.emotions;
                let maxEmotion = null;
                let maxScore = 0;
                Object.entries(emotions).forEach(([emotion, score]) => {
                    if (score > maxScore) {
                        maxScore = score;
                        maxEmotion = emotion;
                    }
                });
                emotionValue = maxEmotion;
            } else if (point.emotion_analysis) {
                emotionValue = point.emotion_analysis.dominant_emotion || point.emotion_analysis;
            }
            
            return {
                second: timeValue,
                dominant_emotion: emotionValue,
                confidence: point.confidence || 1,
                // CRITICAL: Preserve the original emotions objects
                emotions: point.emotions,
                emotion_analysis: point.emotion_analysis
            };
        });
        
        if (validPoints.length === 0) {
            console.warn('generateEmotionTimelineData: No valid time series points found');
            return [];
        }
        
        const maxTime = Math.max(...validPoints.map(point => point.second));
        const numIntervals = Math.ceil(maxTime / intervalDuration);
        
        const timelineData = [];
        
        for (let i = 0; i < numIntervals; i++) {
            const startTime = i * intervalDuration;
            const endTime = (i + 1) * intervalDuration;
            
            const intervalPoints = validPoints.filter(
                point => point.second >= startTime && point.second < endTime
            );
            
            if (intervalPoints.length > 0) {
                const emotionSums = { 
                    happiness: 0, sadness: 0, anger: 0, fear: 0, 
                    surprise: 0, disgust: 0, neutral: 0 
                };
                let validEmotionSets = 0;
                
                intervalPoints.forEach(point => {
                    // Use all emotion values from each segment, not just dominant_emotion
                    let emotions = null;
                    
                    // Priority 1: Use the full emotions object if available
                    if (point.emotions && typeof point.emotions === 'object') {
                        emotions = point.emotions;
                    } 
                    // Priority 2: Check emotion_analysis
                    else if (point.emotion_analysis && typeof point.emotion_analysis === 'object') {
                        emotions = point.emotion_analysis;
                    } 
                    // Fallback: Only if no emotion distribution is available
                    else if (point.dominant_emotion && !point.emotions) {
                        const dominantEmotion = point.dominant_emotion.toLowerCase();
                        const confidence = point.confidence || 0.8;
                        emotions = { [dominantEmotion]: confidence };
                        // Fill remaining emotions with minimal values
                        Object.keys(emotionSums).forEach(emotion => {
                            if (emotion !== dominantEmotion) {
                                emotions[emotion] = (1 - confidence) / 6; // Distribute remaining probability
                            }
                        });
                    }
                    
                    if (emotions) {
                        validEmotionSets++;
                        Object.entries(emotions).forEach(([emotion, value]) => {
                            const emotionKey = emotion?.toLowerCase();
                            if (emotionSums.hasOwnProperty(emotionKey)) {
                                emotionSums[emotionKey] += (typeof value === 'number' ? value : 0);
                            }
                        });
                    }
                });
                
                // Calculate average emotion values for this time interval with translated labels
                const averageEmotions = {
                    timeRange: `${Math.floor(startTime / 60)}:${String(startTime % 60).padStart(2, '0')}-${Math.floor(endTime / 60)}:${String(endTime % 60).padStart(2, '0')}`,
                    [t('AudioEmotionSentiment.Emotions.Happiness', 'Happiness')]: validEmotionSets > 0 ? (emotionSums.happiness / validEmotionSets) * 100 : 0,
                    [t('AudioEmotionSentiment.Emotions.Sadness', 'Sadness')]: validEmotionSets > 0 ? (emotionSums.sadness / validEmotionSets) * 100 : 0,
                    [t('AudioEmotionSentiment.Emotions.Anger', 'Anger')]: validEmotionSets > 0 ? (emotionSums.anger / validEmotionSets) * 100 : 0,
                    [t('AudioEmotionSentiment.Emotions.Fear', 'Fear')]: validEmotionSets > 0 ? (emotionSums.fear / validEmotionSets) * 100 : 0,
                    [t('AudioEmotionSentiment.Emotions.Surprise', 'Surprise')]: validEmotionSets > 0 ? (emotionSums.surprise / validEmotionSets) * 100 : 0,
                    [t('AudioEmotionSentiment.Emotions.Disgust', 'Disgust')]: validEmotionSets > 0 ? (emotionSums.disgust / validEmotionSets) * 100 : 0,
                    [t('AudioEmotionSentiment.Emotions.Neutral', 'Neutral')]: validEmotionSets > 0 ? (emotionSums.neutral / validEmotionSets) * 100 : 0,
                };
                
                timelineData.push(averageEmotions);
            }
        }
        
        return timelineData;
    } catch (error) {
        console.error('Error generating timeline data:', error);
        return [];
    }
}

export default AudioEmotionSentimentTimelineCard;